import * as React from 'react';
import { Form } from 'antd';
import { FormComponentProps, WrappedFormUtils, FormLayout } from 'antd/lib/form/Form';
import bindAllOfThis from "src/utils/BindThisHelper";
import { AntFormItems } from "./FormItems";
import { AntFormRules } from "./Rules";

interface AntFormInternalProps {
    hideRequiredMark?: boolean;
    className?: string;
    layout?: FormLayout;
    /**
     * Runs 'form.validateFields()' after any value has changed
     *
     * Note: This will cause double onChange events to fire, sometimes
     */
    requireOnChangeValidation?: boolean;
    onChange?: (values: any) => void;
    onSubmit?: (values: any) => void;
}

class AntFormInternal extends React.Component<AntFormInternalProps & FormComponentProps> {
    static Items: typeof AntFormItems;
    static Rules: typeof AntFormRules;

    constructor(props) {
        super(props);
        bindAllOfThis(this, AntFormInternal.prototype);
    }

    onSubmit(event: React.FormEvent<any>) {
        event.preventDefault();
        this.props.form.validateFields({ force: true }, (errors, values: any) => {
            // Make sure all fields are filled out
            if (!errors && this.props.onSubmit != null) {
                this.props.onSubmit(values);
            }
        });
    }

    internalOnChange(child: React.ReactElement<AntFormItems.BaseAntFormItemsProps>, value: string) {
        let childOnChange = (child as any).onChange;
        if (childOnChange != null) {
            childOnChange(value);
        }

        if (this.props.requireOnChangeValidation === true) {
            window.setTimeout(() => {
                this.props.form.validateFields({ force: true });
            }, 1);
        }
    }

    internalOnBlur(child: React.ReactElement<AntFormItems.BaseAntFormItemsProps>, value: string) {
        let childOnBlur = (child as any).onBlur;
        if (childOnBlur != null) {
            childOnBlur(value);
        }

        if (this.props.requireOnChangeValidation !== true) {
            window.setTimeout(() => {
                this.props.form.validateFields({ force: true });
            }, 1);
        }
    }

    renderChildren(children: React.ReactNode) {
        let arr = [];
        React.Children.forEach(children, (child: any) => {
            // Escape for non-node elements (eg. pure text)
            if (!child.props) {
                return arr.push(child);
            }

            let namedChild = child as React.ReactElement<AntFormItems.BaseAntFormItemsProps & { onChange: (value: string) => void; onBlur: (value: string) => void; }>;
            if (namedChild.type === AntFormItems.Input) {
                return arr.push(React.cloneElement(namedChild, {
                    form: this.props.form,
                    onChange: value => this.internalOnChange(namedChild, value),
                    onBlur: value => this.internalOnBlur(namedChild, value),
                }));
            } else if (namedChild.type === AntFormItems.Custom) {
                return arr.push(React.cloneElement(namedChild, {
                    form: this.props.form,
                    onChange: value => this.internalOnChange(namedChild, value),
                    onBlur: value => this.internalOnBlur(namedChild, value),
                }));
            } else if (namedChild.type === AntFormItems.SubmitButton) {
                return arr.push(React.cloneElement(namedChild, {
                    form: this.props.form,
                    onChange: value => this.internalOnChange(namedChild, value),
                    onBlur: value => this.internalOnBlur(namedChild, value),
                }));
            // } else if (child.type === 'div' && child.props.children) {
            //     return React.cloneElement(child, {
            //         children: this.renderChildren(child.props.children),
            //     });
            } else if (child.props.children) {
                return arr.push(React.cloneElement(child, undefined, this.renderChildren(child.props.children)));
            }

            return arr.push(child);
        });
        return arr;
    }

    render() {
        let hideRequiredMark = this.props.hideRequiredMark == null ? undefined : this.props.hideRequiredMark;
        let className = this.props.className == null ? undefined : this.props.className;
        let layout = this.props.layout == null ? undefined : this.props.layout;

        // Give children the form prop
        let children = this.renderChildren(this.props.children);

        return <Form hideRequiredMark={hideRequiredMark} onSubmit={this.onSubmit} className={className} layout={layout}>
            {children}
        </Form>;
    }
}

let AntFormSimple = Form.create<AntFormInternalProps>({
    onValuesChange: (props, changedValues, allValues) => {
        if (props.onChange != null) {
            props.onChange(changedValues);
        }
    }
})(AntFormInternal) as React.ComponentClass<AntFormInternalProps>;

// Exporting everything from here, as the form is 'usually' the first to be imported, making the import statement block just a few lines shorter
export { AntFormSimple, AntFormInternal as AntForm, AntFormItems, AntFormRules };



// WEBPACK FOOTER //
// ./src/utils/formUtil/Form.tsx